<template>
  <div
    :title="itemData.name"
    :class="[
      hasIcon && hasThumbnail && 'justify-between',
      hasIcon && !hasThumbnail && 'justify-between',
    ]"
    class="element_card relative flex h-full w-full cursor-pointer flex-col overflow-hidden rounded bg-white shadow-normal shadow-gray-400 hover:shadow-blue-400/50"
    @click="handleClick"
  >
    <figure
      v-if="!hasIcon && hasThumbnail"
      class="relative block aspect-video w-full overflow-hidden rounded"
    >
      <img
        :src="contextThumbnail"
        :alt="itemData.name || itemData.description"
        loading="lazy"
        class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
      />
    </figure>
    <div
      class="flex flex-1 tablet:p-4 desktop:p-5"
      :class="[
        hasIcon && !hasThumbnail && 'flex-col',
        !hasIcon && hasThumbnail && 'flex-col',
      ]"
    >
      <figure
        v-if="hasIcon"
        class="mb-2 h-[46px] w-[46px] basis-[46px] overflow-hidden rounded-lg border border-solid border-gray-400 shadow-normal shadow-gray-400 tablet:h-[78px] tablet:w-[78px] tablet:basis-[78px] tablet:rounded-2xl desktop:h-20 desktop:w-20 desktop:basis-20"
      >
        <img
          :src="contextIconUrl"
          :alt="itemData.name || itemData.description"
          loading="lazy"
          class="pointer-events-none block h-full w-full object-contain"
        />
      </figure>
      <div
        class="flex flex-1 flex-col justify-between"
        :class="hasIcon && hasThumbnail && 'tablet:pl-4 desktop:pl-5'"
      >
        <div class="flex-1">
          <h3
            class="mb-2 line-clamp-2 text-sm font-bold leading-6 tablet:text-base"
          >
            {{ itemData.name }}
          </h3>
          <p class="mb-2 line-clamp-4 w-full text-sm text-gray-600">
            {{ itemData.description }}
          </p>
        </div>
        <div class="flex justify-end">
          <TheNuxtLink
            :title="itemData.name"
            :to="provideFormatUrl(itemData.link)"
            class="element_arrow flex h-8 w-8 items-center justify-center rounded text-blue-400"
          >
            <IconArrow class="h-5 w-5" direction="right" />
          </TheNuxtLink>
        </div>
      </div>
    </div>
    <figure
      v-if="hasIcon && hasThumbnail"
      class="relative block aspect-video w-full overflow-hidden"
    >
      <img
        :src="contextThumbnail"
        :alt="itemData.name || itemData.description"
        loading="lazy"
        class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
      />
    </figure>
    <TheNuxtLink
      v-if="isCN"
      :id="itemData.trackingCode"
      :title="itemData.name"
      :to="provideFormatUrl(itemData.link)"
      class="absolute left-0 top-0 block h-full w-full"
    />
  </div>
</template>

<script setup lang="ts">
  import type { IItem as IListProductItem } from '@/components/Shared/Suite/ListProduct/index.vue';
  import type { IItem as ISuiteProductItem } from '@/components/Shared/Suite/SuiteProduct/index.vue';
  import type { TUrlConnector } from '@/components/Shared/Suite/type.ts';

  interface IProps {
    itemData: IListProductItem | ISuiteProductItem;
    urlConnector?: TUrlConnector;
  }

  const props = defineProps<IProps>();

  const { isCN } = useLanguage();
  const { provideFormatUrl, processLink } = useFormatUrl();
  const { provideResponsiveImgUrl, provideImgUrl } = useSolutionsData();

  const contextThumbnail = computed((): string => {
    const { thumbnail } = props.itemData;
    if (!thumbnail) return '';

    if (props.urlConnector === 'solution') {
      return provideResponsiveImgUrl(thumbnail);
    }
    return thumbnail;
  });

  const contextIconUrl = computed((): string => {
    const { iconThumbnail } = props.itemData;
    if (!iconThumbnail) return '';

    if (props.urlConnector === 'solution') {
      return provideImgUrl(`${iconThumbnail}.webp`);
    }
    return iconThumbnail;
  });

  const hasIcon = computed((): boolean => !!props.itemData.iconThumbnail);

  const hasThumbnail = computed((): boolean => !!props.itemData.thumbnail);

  const handleClick = (): void => {
    if (isCN.value) return;
    processLink(provideFormatUrl(props.itemData.link));
  };
</script>

<style lang="scss" scoped>
  .element_card:hover {
    .element_arrow {
      @apply bg-blue-400 text-white;
    }
  }
</style>
